import type { BrandLoadDiagnostic } from 'api';
import Logistics from 'api/logistics';

const SAFE_ERROR_CODES = new Set([
  'ERR_NETWORK',
  'ECONNABORTED',
  'ETIMEDOUT',
  'ERR_BAD_RESPONSE',
  'ERR_BAD_REQUEST',
  'ERR_INVALID_URL',
  'ERR_BAD_OPTION',
  'ERR_BAD_OPTION_VALUE',
]);
// Only retain fixed entry routes; dynamic IDs and unknown path segments are omitted.
const SAFE_ENTRY_ROUTES = new Set([
  'register',
  'benefit',
  'privacy',
  'terms',
  'redirect',
  'suspended',
  'membercard',
  'pointcards',
  'coupon',
  'home',
  'store',
  'store_value',
  'memberarea',
  'addoa',
  'survey',
  'byurl',
  'system',
  'lang',
  'cart',
]);

export const reportBrandLoadFailure = ({
  brandIdentifier,
  trigger,
  attempts,
  diagnostic,
}: {
  brandIdentifier?: string;
  trigger: 'initial' | 'retry';
  attempts: number;
  diagnostic: BrandLoadDiagnostic;
}): void => {
  if (diagnostic.code === 'ERR_CANCELED') return;

  try {
    // Match the backend format, while redacting numeric-like IDs and LINE UIDs even with @ prefixes.
    const identifier =
      brandIdentifier &&
      /^[a-z0-9@_-]{1,50}$/i.test(brandIdentifier) &&
      !/^@*[_-]*\d[\d_-]*$/.test(brandIdentifier) &&
      !/^@*U[0-9a-f]{32}$/i.test(brandIdentifier)
        ? brandIdentifier
        : null;
    const parts = window.location.pathname.split('/');
    const pagePath =
      window.location.pathname === '/'
        ? '/'
        : identifier && parts[1] === identifier && parts.length === 2
          ? `/${identifier}`
          : identifier &&
              parts[1] === identifier &&
              parts.length === 3 &&
              SAFE_ENTRY_ROUTES.has(parts[2])
            ? `/${identifier}/${parts[2]}`
            : null;
    const env = process.env.REACT_APP_ENV;
    const status = diagnostic.status;
    Logistics.error({
      event: 'brand_load_failed',
      brand_identifier: identifier,
      page_path: pagePath,
      trigger,
      attempt_count: attempts,
      failure_kind: diagnostic.kind,
      http_status:
        typeof status === 'number' &&
        Number.isInteger(status) &&
        status >= 100 &&
        status <= 599
          ? status
          : null,
      error_code:
        diagnostic.code && SAFE_ERROR_CODES.has(diagnostic.code)
          ? diagnostic.code
          : null,
      app_env:
        env === 'prod' || env === 'stage' || env === 'dev' ? env : 'unknown',
    });
  } catch {
    // Diagnostics must never change the brand loading outcome.
  }
};
